import SEO from '../shared/SEO';
import { useSelector, useDispatch } from 'react-redux';
import { addToCart, removeFromCart, deleteFromCart, clearCart, setWholesaleConfig, updateStockValidation } from '../../store/cartSlice';
import { useNavigate } from 'react-router-dom';
import { showConfirm, showWarning } from '../../utils/sweetAlert';
import { useEffect } from 'react';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { getStockIssueMessage, validateCartStock } from '../../utils/stockValidation';

const Cart = () => {
    const cart = useSelector((state) => state.cart.items);
    const total = useSelector((state) => state.cart.total);
    const wholesaleConfig = useSelector((state) => state.cart.wholesaleConfig);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const totalQuantity = cart.reduce((acc, item) => acc + item.quantity, 0);
    const minimumPurchaseQuantity = wholesaleConfig?.minimumPurchaseQuantity || 4;
    const wholesaleMinQuantity = wholesaleConfig?.wholesaleMinQuantity || 10;
    const wholesaleIsActive = wholesaleConfig?.isActive && totalQuantity >= wholesaleMinQuantity;
    const cartSignature = cart.map((item) => `${item._id}:${item.selectedVariant?.variantId || item.selectedVariant?.sku || ''}:${item.quantity}`).join('|');
    const hasStockIssues = cart.some((item) => item.stockIssue);

    useEffect(() => {
        const loadWholesaleConfig = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/wholesale-config`);
                const data = await response.json();

                if (response.ok && data.data) {
                    dispatch(setWholesaleConfig(data.data));
                }
            } catch (error) {
                console.error('Error cargando precios por mayor:', error);
            }
        };

        loadWholesaleConfig();
    }, [dispatch]);

    useEffect(() => {
        if (!cart.length) return;
        let isCurrent = true;

        const refreshStock = async () => {
            try {
                const result = await validateCartStock(cart);
                if (!isCurrent) return;
                dispatch(updateStockValidation(result));
                if (result.issues?.length) {
                    showWarning(getStockIssueMessage(result.issues), 'Hay productos agotados en tu carrito');
                }
            } catch (error) {
                console.error('Error verificando el stock del carrito:', error);
            }
        };

        refreshStock();
        return () => { isCurrent = false; };
    }, [cartSignature, dispatch]);

    const handleCheckout = async () => {
        try {
            const result = await validateCartStock(cart);
            dispatch(updateStockValidation(result));
            if (!result.valid) {
                showWarning(getStockIssueMessage(result.issues), 'Hay productos agotados en tu carrito');
                return;
            }
        } catch (error) {
            showWarning('No pudimos verificar el stock. Intentá nuevamente en unos segundos.', 'Stock no disponible');
            return;
        }

        if (totalQuantity < minimumPurchaseQuantity) {
            showWarning(
                `Para comprar, necesitás tener al menos ${minimumPurchaseQuantity} productos en el carrito. Actualmente tenés ${totalQuantity}.`,
                'Cantidad mínima requerida'
            );
            return;
        }
        navigate('/checkout'); 
    };

    const handleClearCart = async () => {
        const result = await showConfirm(
            '¿Vaciar carrito?',
            'Esta acción eliminará todos los productos del carrito',
            'Sí, vaciar',
            'Cancelar'
        );
        if (result) {
            dispatch(clearCart());
        }
    };

    const handleDeleteItem = async (item) => {
        const result = await showConfirm(
            '¿Eliminar producto?',
            `¿Estás seguro de que deseas eliminar "${item.name}" del carrito?`,
            'Sí, eliminar',
            'Cancelar'
        );
        if (result) {
            dispatch(deleteFromCart(item));
        }
    };

    if (cart.length === 0) {
        return (
            <>
              <SEO title="Carrito" />
            <div className="container wrapper mt-5">
                <div className="text-center py-5">
                    <i className="fa-solid fa-cart-shopping" style={{ fontSize: '4rem', color: '#ccc', marginBottom: '20px' }}></i>
                    <h2 className="mb-3">Tu carrito está vacío</h2>
                    <p className="text-muted mb-4">¡Agrega algunos productos increíbles!</p>
                    <button 
                        className="add-to-cart-btn px-5 py-3 shadow-sm"
                        style={{ fontSize: '1rem', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 'bold', margin: '0 auto' }}
                        onClick={() => navigate('/tienda')}
                    >
                        <i className="fa-solid fa-store me-2"></i>
                        Ir a la tienda
                    </button>
                </div>
            </div>
            </>
        );
    }

    return (
        <>
          <SEO title="Carrito" />
        <div className="container wrapper mt-5 mb-5 cart-page-modern">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h1 className="h3 mb-0 fw-bold cart-page-title">
                    <i className="fa-solid fa-cart-shopping me-3 cart-page-icon"></i>
                    Carrito de Compras
                </h1>
                <span className="cart-count-badge">
                    {cart.reduce((acc, item) => acc + item.quantity, 0)} productos
                </span>
            </div>

            <div className="row g-4">
                {/* Columna Izquierda: Lista de Productos */}
                <div className="col-lg-8">
                    <div className="cart-items-container d-flex flex-column gap-3">
                        {cart.map((item) => (
                            <div key={`${item._id}-${item.selectedVariant?.sku || 'no-variant'}`} className="card border-0 shadow-sm rounded-4 overflow-hidden cart-item-card">
                                <div className="card-body p-3">
                                    <div className="row align-items-center">
                                        {/* Imagen */}
                                        <div className="col-3 col-md-2">
                                            <div className="position-relative ratio ratio-1x1 overflow-hidden bg-light cart-item-thumb">
                                                <img 
                                                    src={getDisplayImageUrl(item.selectedVariant?.images?.[0] || item.images?.[0] || item.image)} 
                                                    alt={item.name}
                                                    className="object-fit-cover"
                                                    style={{ width: '100%', height: '100%' }}
                                                    onError={(e) => { e.target.src = '/placeholder-image.svg'; }}
                                                />
                                            </div>
                                        </div>

                                        {/* Detalles */}
                                        <div className="col-9 col-md-5">
                                            <h5 className="mb-1 text-truncate fw-bold" title={item.name}>{item.name}</h5>
                                            <div className="d-flex flex-wrap gap-2 mb-1">
                                                {item.selectedVariant ? (
                                                    <>
                                                        <span className="badge cart-variant-chip">{item.selectedVariant.size}</span>
                                                        <span className="badge cart-variant-chip">{item.selectedVariant.color}</span>
                                                    </>
                                                ) : item.variantInfo && (
                                                    <span className="text-muted small">{item.variantInfo}</span>
                                                )}
                                            </div>
                                            <div className="cart-item-amount fw-bold d-md-none mt-1">
                                                ${item.price * item.quantity}
                                            </div>
                                            {item.stockIssue && (
                                                <small className="text-danger fw-semibold d-block mt-1">
                                                    Sin stock momentáneamente. Eliminá este producto para continuar.
                                                </small>
                                            )}
                                            {item.isWholesalePrice && (
                                                <small className="text-success fw-semibold d-block mt-1">
                                                    Precio comerciantes aplicado
                                                </small>
                                            )}
                                        </div>

                                        {/* Cantidad y Precio (Desktop) */}
                                        <div className="col-12 col-md-5 mt-3 mt-md-0">
                                            <div className="d-flex align-items-center justify-content-between justify-content-md-end gap-4">
                                                <div className="input-group input-group-sm cart-quantity-group" style={{ width: '110px' }}>
                                                    <button 
                                                        className="btn cart-quantity-btn" 
                                                        onClick={() => dispatch(removeFromCart(item))}
                                                    >
                                                        <i className="fa-solid fa-minus"></i>
                                                    </button>
                                                    <span className="form-control text-center bg-white cart-quantity-value">
                                                        {item.quantity}
                                                    </span>
                                                    <button 
                                                        className="btn cart-quantity-btn"
                                                        onClick={() => dispatch(addToCart({ ...item, quantity: 1 }))}
                                                        disabled={Boolean(item.stockIssue) || (item.stockLimit != null && item.quantity >= item.stockLimit)}
                                                        title={item.stockIssue ? 'Este producto está agotado' : item.stockLimit != null && item.quantity >= item.stockLimit ? `Máximo disponible: ${item.stockLimit}` : undefined}
                                                    >
                                                        <i className="fa-solid fa-plus"></i>
                                                    </button>
                                                </div>
                                                {item.stockLimit != null && item.quantity >= item.stockLimit && (
                                                    <small style={{ color: '#e53935', fontSize: '0.7rem', display: 'block', textAlign: 'center', marginTop: '2px' }}>
                                                        Máx. {item.stockLimit}
                                                    </small>
                                                )}

                                                <div className="text-end d-none d-md-block" style={{ minWidth: '80px' }}>
                                                    <div className="fw-bold fs-5 cart-item-amount">${item.price * item.quantity}</div>
                                                    {item.quantity > 1 && (
                                                        <small className="text-muted">${item.price} c/u</small>
                                                    )}
                                                    {item.isWholesalePrice && (
                                                        <small className="text-success d-block">
                                                            Antes ${item.regularPrice} c/u
                                                        </small>
                                                    )}
                                                </div>

                                                <button
                                                    className="btn btn-link cart-remove-btn p-0"
                                                    onClick={() => handleDeleteItem(item)}
                                                    title="Eliminar"
                                                >
                                                    <i className="fa-solid fa-trash-can fa-lg"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <button 
                            className="btn btn-link text-decoration-none px-0 cart-continue-link"
                            onClick={() => navigate('/tienda')}
                        >
                            <i className="fa-solid fa-arrow-left me-2"></i>
                            Seguir comprando
                        </button>
                        
                        <button 
                            className="btn btn-sm px-3 cart-clear-btn"
                            onClick={handleClearCart}
                        >
                            <i className="fa-solid fa-trash me-2"></i>
                            Vaciar carrito
                        </button>
                    </div>
                </div>

                {/* Columna Derecha: Resumen (Sticky) */}
                <div className="col-lg-4">
                    <div className="card border-0 shadow rounded-4 sticky-top cart-summary-card" style={{ top: '2rem', zIndex: 10 }}>
                        <div className="card-body p-4">
                            <h5 className="card-title fw-bold mb-4">Resumen del pedido</h5>
                            <div className={`alert ${totalQuantity >= minimumPurchaseQuantity ? 'alert-success' : 'alert-danger'} py-2 mb-3`}>
                                <i className={`fas ${totalQuantity >= minimumPurchaseQuantity ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2`}></i>
                                {totalQuantity >= minimumPurchaseQuantity
                                    ? 'Mínimo de compra alcanzado. Podés continuar.'
                                    : `Mínimo requerido: ${minimumPurchaseQuantity} productos. Te ${minimumPurchaseQuantity - totalQuantity === 1 ? 'falta' : 'faltan'} ${minimumPurchaseQuantity - totalQuantity} más.`}
                            </div>
                            {wholesaleConfig?.isActive && (
                                <div className={`alert ${wholesaleIsActive ? 'alert-success' : 'alert-light'} py-2 mb-3`}>
                                    {wholesaleIsActive
                                        ? 'Precios comerciantes aplicados'
                                        : `Agregá ${Math.max(wholesaleMinQuantity - totalQuantity, 0)} producto(s) más para precio comerciantes`}
                                </div>
                            )}
                            
                            <div className="d-flex justify-content-between mb-3">
                                <span className="text-muted">Subtotal</span>
                                <span className="fw-bold">${total}</span>
                            </div>
                            
                            <div className="d-flex justify-content-between mb-3">
                                <span className="text-muted">Envío</span>
                                <span className="cart-summary-note fw-bold">Calculado en el checkout</span>
                            </div>

                            <hr className="my-3" />

                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <span className="fs-5 fw-bold">Total</span>
                                <span className="fs-4 fw-bold cart-summary-total">${total}</span>
                            </div>

                            <button 
                                className="add-to-cart-btn w-100 py-3 shadow-sm mb-3"
                                style={{ fontSize: '1rem', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 'bold' }}
                                onClick={handleCheckout}
                                disabled={hasStockIssues || totalQuantity < minimumPurchaseQuantity}
                            >
                                <i className="fa-solid fa-cart-shopping me-2"></i>
                                PROCEDER AL CHECKOUT
                            </button>

                            <div className="text-center">
                                <div className="d-flex justify-content-center gap-3 text-muted mb-2" style={{ fontSize: '1.5rem' }}>
                                    <i className="fa-brands fa-cc-visa"></i>
                                    <i className="fa-brands fa-cc-mastercard"></i>
                                    <i className="fa-brands fa-cc-amex"></i>
                                    <i className="fa-solid fa-shield-halved"></i>
                                </div>
                                <small className="text-muted">
                                    <i className="fa-solid fa-lock me-1"></i>
                                    Pago 100% seguro y encriptado
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default Cart;
